import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  UserIcon, DocumentIcon, LockIcon, EditIcon,
  CheckIcon, AlertIcon, InfoIcon, UploadIcon, CameraIcon,
  ClockIcon,
} from '../../components/Icons';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const token = () => localStorage.getItem('kialuga_token');

const apiCall = async (endpoint, method = 'GET', body = null) => {
  const opts = {
    method,
    headers: { Authorization: `Bearer ${token()}` },
  };
  if (body && !(body instanceof FormData)) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  } else if (body instanceof FormData) {
    opts.body = body;
  }
  const res  = await fetch(`${BASE}${endpoint}`, opts);
  const json = await res.json();
  if (!res.ok) throw new Error(json.mensagem || 'Erro');
  return json;
};

export default function Perfil() {
  const { utilizador, login } = useAuth();
  const [dados,    setDados]    = useState(null);
  const [stats,    setStats]    = useState(null);
  const [tab,      setTab]      = useState('perfil');
  const [loading,  setLoading]  = useState(false);
  const [erro,     setErro]     = useState('');
  const [sucesso,  setSucesso]  = useState('');

  const [nome,     setNome]     = useState('');
  const [telefone, setTelefone] = useState('');
  const [tipo,     setTipo]     = useState('');

  const [biNumero,     setBiNumero]     = useState('');
  const [biFoto,       setBiFoto]       = useState(null);
  const [selfie,       setSelfie]       = useState(null);
  const [prevBi,       setPrevBi]       = useState(null);
  const [prevSelf,     setPrevSelf]     = useState(null);
  const [cameraAberta, setCameraAberta] = useState(false);
  const [cameraErro,   setCameraErro]   = useState('');
  const videoRef   = useRef();
  const canvasRef  = useRef();

  const [pwAtual,  setPwAtual]  = useState('');
  const [pwNova,   setPwNova]   = useState('');
  const [pwConf,   setPwConf]   = useState('');

  const avatarRef = useRef();

  useEffect(() => { carregarPerfil(); }, []);

  const carregarPerfil = async () => {
    try {
      const res = await apiCall('/perfil');
      setDados(res.utilizador);
      setStats(res.estatisticas);
      setNome(res.utilizador.nome || '');
      setTelefone(res.utilizador.telefone || '');
      setTipo(res.utilizador.tipo || 'cliente');
      setBiNumero(res.utilizador.bi_numero || '');
    } catch (err) {
      setErro(err.message);
    }
  };

  const msg = (tipo, texto) => {
    if (tipo === 'erro') { setErro(texto); setSucesso(''); }
    else { setSucesso(texto); setErro(''); }
    setTimeout(() => { setErro(''); setSucesso(''); }, 4000);
  };

  const guardarPerfil = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await apiCall('/perfil', 'PUT', { nome, telefone, tipo });
      login(token(), res.utilizador);
      await carregarPerfil();
      msg('sucesso', 'Perfil actualizado com sucesso!');
    } catch (err) {
      msg('erro', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await apiCall('/perfil/avatar', 'POST', fd);
      await carregarPerfil();
      login(token(), { ...utilizador, foto_url: res.foto_url });
      msg('sucesso', 'Foto actualizada!');
    } catch (err) {
      msg('erro', err.message);
    } finally {
      setLoading(false);
    }
  };

  const submeterKyc = async (e) => {
    e.preventDefault();
    if (!biFoto || !selfie) return msg('erro', 'Foto do BI e selfie são obrigatórios');
    if (!biNumero)          return msg('erro', 'Número do BI é obrigatório');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('bi_numero', biNumero);
      fd.append('bi_foto',   biFoto);
      fd.append('selfie',    selfie);
      await apiCall('/perfil/kyc', 'POST', fd);
      await carregarPerfil();
      msg('sucesso', 'Documentos submetidos! Verificação em até 24h.');
    } catch (err) {
      msg('erro', err.message);
    } finally {
      setLoading(false);
    }
  };

  const mudarPassword = async (e) => {
    e.preventDefault();
    if (pwNova !== pwConf) return msg('erro', 'As passwords não coincidem');
    if (pwNova.length < 6) return msg('erro', 'Mínimo 6 caracteres');
    setLoading(true);
    try {
      await apiCall('/perfil/password', 'PUT', { password_atual: pwAtual, password_nova: pwNova });
      setPwAtual(''); setPwNova(''); setPwConf('');
      msg('sucesso', 'Password alterada com sucesso!');
    } catch (err) {
      msg('erro', err.message);
    } finally {
      setLoading(false);
    }
  };

  const previewFoto = (file, setter) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => setter(e.target.result);
    reader.readAsDataURL(file);
  };

  const abrirCamera = async () => {
    setCameraErro('');
    setCameraAberta(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      setCameraErro('Não foi possível aceder à câmara. Verifica as permissões do browser.');
      setCameraAberta(false);
    }
  };

  const fecharCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setCameraAberta(false);
    setCameraErro('');
  };

  const tirarFoto = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      setSelfie(file);
      setPrevSelf(canvas.toDataURL('image/jpeg'));
      fecharCamera();
    }, 'image/jpeg', 0.9);
  };

  const kycCores = {
    pendente:   { bg:'#fffbeb', border:'#fde68a', cor:'#d97706', label:'Verificação pendente',   icon:<ClockIcon size={13} color='#d97706'/> },
    verificado: { bg:'#f0fdf4', border:'#86efac', cor:'#16a34a', label:'Identidade verificada',  icon:<CheckIcon size={13} color='#16a34a'/> },
    rejeitado:  { bg:'#fef2f2', border:'#fecaca', cor:'#dc2626', label:'Verificação rejeitada',   icon:<AlertIcon size={13} color='#dc2626'/> },
  };

  const kycInfo = dados ? (kycCores[dados.kyc_estado] || kycCores.pendente) : kycCores.pendente;

  const inp = {
    width:'100%', padding:'10px 12px',
    background:'var(--surface)', border:'1px solid var(--border)',
    borderRadius:'8px', fontSize:'13px', color:'var(--text-primary)',
    fontFamily:'inherit', outline:'none',
  };
  const lbl = { fontSize:'12px', fontWeight:'500', color:'var(--text-secondary)', display:'block', marginBottom:'5px' };

  const tabs = [
    { id:'perfil',    label:'Perfil',          icon:<UserIcon size={14}/> },
    { id:'kyc',       label:'Verificação KYC', icon:<DocumentIcon size={14}/> },
    { id:'seguranca', label:'Segurança',        icon:<LockIcon size={14}/> },
  ];

  if (!dados) return (
    <>
      <div className="topbar"><div><div className="topbar-title">O meu perfil</div></div></div>
      <div className="page-content" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'300px'}}>
        <div style={{textAlign:'center',color:'var(--text-muted)'}}>
          <ClockIcon size={32} color='var(--border)'/>
          <div style={{marginTop:'10px'}}>A carregar perfil...</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">O meu perfil</div>
          <div className="topbar-sub">Gere as tuas informações e verificação</div>
        </div>
      </div>

      <div className="page-content">
        <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:'20px',alignItems:'start'}}>

          {/* Coluna esquerda */}
          <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>

            {/* Card avatar */}
            <div className="card" style={{textAlign:'center',padding:'24px 20px'}}>
              <div style={{position:'relative',width:'80px',margin:'0 auto 16px',cursor:'pointer'}}
                onClick={()=>avatarRef.current?.click()}>
                {dados.foto_url
                  ? <img src={dados.foto_url} alt="avatar" style={{width:'80px',height:'80px',borderRadius:'50%',objectFit:'cover',border:'3px solid var(--border)'}} />
                  : <div style={{width:'80px',height:'80px',borderRadius:'50%',background:'var(--orange)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'28px',fontWeight:'700',color:'#fff',margin:'0 auto',border:'3px solid var(--border)'}}>
                      {(dados.nome||'?')[0].toUpperCase()}
                    </div>
                }
                <div style={{position:'absolute',bottom:0,right:0,width:'24px',height:'24px',background:'var(--orange)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid white'}}>
                  <EditIcon size={11} color='#fff'/>
                </div>
              </div>
              <input ref={avatarRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleAvatar} />
              <div style={{fontSize:'15px',fontWeight:'600',color:'var(--text-primary)',marginBottom:'2px'}}>{dados.nome || 'Sem nome'}</div>
              <div style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'12px',textTransform:'capitalize'}}>{dados.tipo}</div>
              <div style={{background:kycInfo.bg,border:`1px solid ${kycInfo.border}`,borderRadius:'8px',padding:'7px 12px',fontSize:'12px',fontWeight:'500',color:kycInfo.cor,display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                {kycInfo.icon} {kycInfo.label}
              </div>
            </div>

            {/* Estatísticas */}
            {stats && (
              <div className="card">
                <div style={{fontSize:'13px',fontWeight:'600',marginBottom:'12px',color:'var(--text-primary)'}}>Estatísticas</div>
                {[
                  ['Reservas como cliente',     stats.total_como_cliente],
                  ['Reservas como fornecedor',  stats.total_como_fornecedor],
                  ['Anúncios ativos',           stats.anuncios_ativos],
                  ['Total ganhos',              `${parseFloat(stats.total_ganhos||0).toLocaleString()} Kz`],
                ].map(([l,v])=>(
                  <div key={l} style={{display:'flex',justifyContent:'space-between',fontSize:'12px',padding:'5px 0',borderBottom:'1px solid var(--border)'}}>
                    <span style={{color:'var(--text-secondary)'}}>{l}</span>
                    <span style={{fontWeight:'600',color:'var(--text-primary)'}}>{v}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div className="card" style={{padding:'6px'}}>
              {tabs.map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)} style={{
                  width:'100%',padding:'9px 12px',border:'none',borderRadius:'8px',
                  textAlign:'left',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',
                  fontWeight: tab===t.id?'600':'400',
                  background: tab===t.id?'var(--orange-light)':'transparent',
                  color: tab===t.id?'var(--orange-dark)':'var(--text-secondary)',
                  marginBottom:'2px',display:'flex',alignItems:'center',gap:'8px',
                }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Coluna direita */}
          <div>
            {/* Alertas */}
            {erro    && <div style={{background:'var(--danger-bg)',border:'1px solid var(--danger-border)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'var(--danger)',display:'flex',gap:'8px',alignItems:'center'}}><AlertIcon size={14}/>{erro}</div>}
            {sucesso && <div style={{background:'var(--success-bg)',border:'1px solid var(--success-border)',borderRadius:'10px',padding:'12px 16px',marginBottom:'16px',fontSize:'13px',color:'var(--success)',display:'flex',gap:'8px',alignItems:'center'}}><CheckIcon size={14}/>{sucesso}</div>}

            {/* TAB PERFIL */}
            {tab==='perfil' && (
              <div className="card">
                <div style={{fontSize:'15px',fontWeight:'600',marginBottom:'20px',paddingBottom:'12px',borderBottom:'1px solid var(--border)'}}>
                  Informações pessoais
                </div>
                <form onSubmit={guardarPerfil} style={{display:'flex',flexDirection:'column',gap:'16px'}}>
                  <div className="grid-2">
                    <div>
                      <label style={lbl}>Nome completo</label>
                      <input style={inp} type="text" placeholder="O teu nome" value={nome} onChange={e=>setNome(e.target.value)} />
                    </div>
                    <div>
                      <label style={lbl}>Telefone</label>
                      <input style={inp} type="tel" placeholder="+244 9XX XXX XXX" value={telefone} onChange={e=>setTelefone(e.target.value)} />
                    </div>
                    <div>
                      <label style={lbl}>Email</label>
                      <input style={{...inp,background:'var(--surface)',color:'var(--text-muted)',cursor:'not-allowed'}} type="email" value={dados.email||''} readOnly />
                    </div>
                    <div>
                      <label style={lbl}>Tipo de conta</label>
                      <select style={inp} value={tipo} onChange={e=>setTipo(e.target.value)}>
                        <option value="cliente">Cliente</option>
                        <option value="fornecedor">Fornecedor</option>
                      </select>
                    </div>
                  </div>
                  <div style={{display:'flex',justifyContent:'flex-end',paddingTop:'8px',borderTop:'1px solid var(--border)'}}>
                    <button type="submit" disabled={loading} className="btn btn-primary" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                      <CheckIcon size={13}/> {loading?'A guardar...':'Guardar alterações'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* TAB KYC */}
            {tab==='kyc' && (
              <div className="card">
                <div style={{fontSize:'15px',fontWeight:'600',marginBottom:'8px',paddingBottom:'12px',borderBottom:'1px solid var(--border)'}}>
                  Verificação de identidade (KYC)
                </div>
                <div style={{background:kycInfo.bg,border:`1px solid ${kycInfo.border}`,borderRadius:'8px',padding:'12px 16px',marginBottom:'20px',fontSize:'13px',color:kycInfo.cor,lineHeight:'1.6',display:'flex',gap:'8px',alignItems:'flex-start'}}>
                  {kycInfo.icon}
                  <div>
                    <strong>{kycInfo.label}</strong><br/>
                    {dados.kyc_estado==='pendente'   && 'Submete o teu BI e uma selfie para verificar a tua identidade.'}
                    {dados.kyc_estado==='verificado'  && 'A tua identidade está verificada. Tens acesso completo à plataforma.'}
                    {dados.kyc_estado==='rejeitado'   && 'Os teus documentos foram rejeitados. Tenta novamente com documentos mais nítidos.'}
                  </div>
                </div>

                {dados.kyc_estado !== 'verificado' && (
                  <form onSubmit={submeterKyc} style={{display:'flex',flexDirection:'column',gap:'16px'}}>
                    <div>
                      <label style={lbl}>Número do BI / Passaporte *</label>
                      <input style={inp} type="text" placeholder="Ex: 001234567LA041" value={biNumero} onChange={e=>setBiNumero(e.target.value)} />
                    </div>

                    <div className="grid-2">
                      {/* Upload BI */}
                      <div>
                        <label style={lbl}>Foto do BI / Passaporte *</label>
                        <div style={{border:'2px dashed var(--border)',borderRadius:'10px',overflow:'hidden',cursor:'pointer',transition:'all 0.15s'}}
                          onClick={()=>document.getElementById('bi-upload').click()}
                          onMouseOver={e=>e.currentTarget.style.borderColor='var(--orange)'}
                          onMouseOut={e=>e.currentTarget.style.borderColor='var(--border)'}>
                          {prevBi
                            ? <img src={prevBi} alt="BI" style={{width:'100%',height:'140px',objectFit:'cover'}} />
                            : <div style={{height:'140px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'8px',background:'var(--surface)'}}>
                                <DocumentIcon size={32} color='var(--text-muted)'/>
                                <span style={{fontSize:'12px',color:'var(--text-muted)'}}>Clica para escolher</span>
                              </div>
                          }
                        </div>
                        <input id="bi-upload" type="file" accept="image/*" style={{display:'none'}} onChange={e=>{setBiFoto(e.target.files[0]);previewFoto(e.target.files[0],setPrevBi);}} />
                      </div>

                      {/* Selfie com câmara ou upload */}
                      <div>
                        <label style={lbl}>Selfie (rosto visível) *</label>

                        {/* Preview da selfie tirada */}
                        {prevSelf && !cameraAberta && (
                          <div style={{border:'2px solid var(--success)',borderRadius:'10px',overflow:'hidden',marginBottom:'8px',position:'relative'}}>
                            <img src={prevSelf} alt="selfie" style={{width:'100%',height:'140px',objectFit:'cover'}}/>
                            <button type="button" onClick={() => { setSelfie(null); setPrevSelf(null); }}
                              style={{position:'absolute',top:'6px',right:'6px',width:'24px',height:'24px',borderRadius:'50%',background:'rgba(0,0,0,0.6)',border:'none',cursor:'pointer',color:'#fff',fontSize:'14px',lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
                          </div>
                        )}

                        {/* Câmara aberta */}
                        {cameraAberta && (
                          <div style={{border:'2px solid var(--orange)',borderRadius:'10px',overflow:'hidden',marginBottom:'8px',position:'relative',background:'#000'}}>
                            <video ref={videoRef} style={{width:'100%',height:'180px',objectFit:'cover',display:'block'}} autoPlay playsInline muted/>
                            <canvas ref={canvasRef} style={{display:'none'}}/>
                            <div style={{position:'absolute',bottom:'8px',left:0,right:0,display:'flex',gap:'8px',justifyContent:'center'}}>
                              <button type="button" onClick={fecharCamera}
                                style={{padding:'7px 14px',background:'rgba(0,0,0,0.6)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontFamily:'inherit'}}>
                                Cancelar
                              </button>
                              <button type="button" onClick={tirarFoto}
                                style={{padding:'7px 18px',background:'var(--orange)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'600',fontFamily:'inherit',display:'flex',alignItems:'center',gap:'5px'}}>
                                <CameraIcon size={13}/> Tirar foto
                              </button>
                            </div>
                          </div>
                        )}

                        {!cameraAberta && !prevSelf && (
                          <div style={{border:'2px dashed var(--border)',borderRadius:'10px',overflow:'hidden',background:'var(--surface)'}}>
                            <div style={{height:'100px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                              <CameraIcon size={28} color='var(--text-muted)'/>
                              <span style={{fontSize:'12px',color:'var(--text-muted)'}}>Nenhuma selfie</span>
                            </div>
                          </div>
                        )}

                        {cameraErro && <div style={{fontSize:'11px',color:'var(--danger)',marginTop:'4px'}}>{cameraErro}</div>}

                        <div style={{display:'flex',gap:'6px',marginTop:'8px'}}>
                          <button type="button" onClick={abrirCamera}
                            style={{flex:1,padding:'8px',background:'var(--orange)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'12px',fontWeight:'600',display:'flex',alignItems:'center',justifyContent:'center',gap:'5px'}}>
                            <CameraIcon size={13}/> Usar câmara
                          </button>
                          <button type="button" onClick={() => document.getElementById('selfie-upload').click()}
                            style={{flex:1,padding:'8px',background:'var(--surface)',color:'var(--text-secondary)',border:'1px solid var(--border)',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'12px',display:'flex',alignItems:'center',justifyContent:'center',gap:'5px'}}>
                            <UploadIcon size={13}/> Carregar ficheiro
                          </button>
                        </div>
                        <input id="selfie-upload" type="file" accept="image/*" capture="user" style={{display:'none'}}
                          onChange={e=>{setSelfie(e.target.files[0]);previewFoto(e.target.files[0],setPrevSelf);}} />
                      </div>
                    </div>

                    <div style={{background:'var(--info-bg)',border:'1px solid var(--info-border)',borderRadius:'8px',padding:'10px 14px',fontSize:'12px',color:'var(--info)',lineHeight:'1.6',display:'flex',gap:'8px',alignItems:'flex-start'}}>
                      <InfoIcon size={14} color='var(--info)' style={{flexShrink:0,marginTop:'1px'}}/>
                      <span>Os teus documentos são tratados de forma confidencial e apenas usados para verificação de identidade. A verificação demora até 24 horas.</span>
                    </div>

                    <div style={{display:'flex',justifyContent:'flex-end',paddingTop:'8px',borderTop:'1px solid var(--border)'}}>
                      <button type="submit" disabled={loading} className="btn btn-primary" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                        <UploadIcon size={13}/> {loading?'A submeter...':'Submeter para verificação'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* TAB SEGURANÇA */}
            {tab==='seguranca' && (
              <div className="card">
                <div style={{fontSize:'15px',fontWeight:'600',marginBottom:'20px',paddingBottom:'12px',borderBottom:'1px solid var(--border)'}}>
                  Alterar password
                </div>
                {dados.google_id && !dados.password_hash ? (
                  <div style={{background:'var(--info-bg)',border:'1px solid var(--info-border)',borderRadius:'8px',padding:'14px 16px',fontSize:'13px',color:'var(--info)',display:'flex',gap:'8px',alignItems:'flex-start'}}>
                    <InfoIcon size={14} color='var(--info)' style={{flexShrink:0,marginTop:'1px'}}/>
                    <span>A tua conta usa login com Google. Não tem password definida.</span>
                  </div>
                ) : (
                  <form onSubmit={mudarPassword} style={{display:'flex',flexDirection:'column',gap:'14px'}}>
                    <div>
                      <label style={lbl}>Password actual *</label>
                      <input style={inp} type="password" placeholder="••••••••" value={pwAtual} onChange={e=>setPwAtual(e.target.value)} />
                    </div>
                    <div>
                      <label style={lbl}>Nova password *</label>
                      <input style={inp} type="password" placeholder="Mínimo 6 caracteres" value={pwNova} onChange={e=>setPwNova(e.target.value)} />
                    </div>
                    <div>
                      <label style={lbl}>Confirmar nova password *</label>
                      <input style={{...inp,borderColor: pwConf&&pwNova!==pwConf?'var(--danger)':'var(--border)'}} type="password" placeholder="Repete a nova password" value={pwConf} onChange={e=>setPwConf(e.target.value)} />
                      {pwConf && pwNova !== pwConf && <div style={{fontSize:'11px',color:'var(--danger)',marginTop:'3px'}}>As passwords não coincidem</div>}
                    </div>
                    <div style={{display:'flex',justifyContent:'flex-end',paddingTop:'8px',borderTop:'1px solid var(--border)'}}>
                      <button type="submit" disabled={loading} className="btn btn-primary" style={{display:'flex',alignItems:'center',gap:'6px'}}>
                        <LockIcon size={13}/> {loading?'A alterar...':'Alterar password'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
